/**
 * One place that knows how a thumbnail URL is built, which widths exist, and
 * what to try when one 404s.
 *
 * Before this module the string `${assetBase}/template-thumbnails/${dir}/${id}.png`
 * was rebuilt by hand in five files, each pairing it with its own copy of an
 * inline `onerror` regex that retried `preview.png`. That duplication is why the
 * grid shipped raw 1280px screenshots — 721 KB per card into a 229x143 box —
 * long after the cost was known: there was no single edit that could fix it.
 *
 * `build-thumbnail-derivatives.js` writes two downscaled WebP siblings next to
 * every source PNG, and the descriptors here point at those with a `srcset` so
 * the browser picks by DPR:
 *
 *   hero.sm.webp  400w    hero.md.webp  960w    hero.png  (fallback, full size)
 *
 * The PNG stays in the ladder deliberately. A template captured but not yet run
 * through the derivative pass resolves to exactly its old behaviour, so the
 * retry ladder needs no metadata. An optional source-dimension manifest enables
 * framing and original-width candidates only after that asset has arrived.
 */

import { escapeHTML } from './html-escape.js';

/** Must match VARIANTS in scripts/parsers/build-thumbnail-derivatives.js. */
const SRCSET_WIDTHS = [
  { suffix: '.sm.webp', width: 400 },
  { suffix: '.md.webp', width: 960 }
];

/**
 * Widest rendered box across every thumbnail surface (the template card at
 * ~230px, doubled for retina headroom). Callers with a materially smaller box —
 * the 48px section-list row — pass their own so the browser can stay on the
 * 400w candidate at every DPR.
 */
const DEFAULT_SIZES = '230px';

const FALLBACK_ATTR = 'data-pb-thumb-fallbacks';
/**
 * Marks an image whose pixels have not arrived. ui/feedback.js owns the visual
 * (and clears it on load) — the kit owns skeletons; this module only stamps it,
 * because this is where an <img> is wired.
 */
const LOADING_ATTR = 'data-pb-thumb-loading';
/** Set once every candidate has failed. Makes give-up idempotent. */
export const THUMB_FAILED_ATTR = 'data-pb-thumb-failed';
const handledErrorEvents = new WeakSet();

function assetBase() {
  const raw = typeof window !== 'undefined' && window.__PB_ASSET_BASE ? window.__PB_ASSET_BASE : '';
  return String(raw).replace(/\/$/, '') || '.';
}

/** `2atpainting.com` → `2atpainting`. Template dirs drop the TLD. */
export function templateDirName(siteName) {
  return String(siteName || '').replace(/\.\w+$/, '');
}

// The metadata is optional at rollout: a missing manifest keeps wide framing
// and omits the unknown original-width srcset candidate. Cache by asset base,
// never by project state; captures are immutable inputs for this app session.
let captureManifest = new Map();
let captureManifestBase = null;
let captureManifestRequest = null;

export async function loadThumbnailManifest() {
  const base = assetBase();
  if (captureManifestBase !== base) {
    captureManifestBase = base;
    captureManifest = new Map();
    captureManifestRequest = null;
  }
  if (!captureManifestRequest) {
    captureManifestRequest = (async () => {
      const { loadJSON } = await import('../core/asset-loader.js');
      try {
        const data = await loadJSON('data/thumbnail-manifest.json');
        const next = new Map();
        if (
          data?.version !== 1 ||
          !data.captures ||
          typeof data.captures !== 'object' ||
          Array.isArray(data.captures)
        ) {
          throw new Error('Invalid thumbnail manifest');
        }
        for (const [key, capture] of Object.entries(data.captures)) {
          if (
            !Number.isSafeInteger(capture?.width) ||
            capture.width <= 0 ||
            !Number.isSafeInteger(capture?.height) ||
            capture.height <= 0
          )
            continue;
          next.set(key, Object.freeze({ width: capture.width, height: capture.height }));
        }
        if (captureManifestBase === base) captureManifest = next;
        return true;
      } catch (err) {
        const { reportError } = await import('../core/report-error.js');
        reportError('thumbnail-manifest', err, { user: 'silent' });
        return false; // Expected rollout fallback: previews work without metadata.
      }
    })();
  }
  return captureManifestRequest;
}

/**
 * Build a descriptor from a logical PNG key (no leading slash, relative to the
 * asset base). `extraFallbacks` are further logical keys to try, in order, once
 * the PNG itself fails.
 *
 * @returns {{src: string, source: string, srcset: string, sizes: string, fallbacks: string[]}}
 */
export function descriptorFor(
  logicalKey,
  { sizes = DEFAULT_SIZES, includeSource = false, extraFallbacks = [] } = {}
) {
  const base = assetBase();
  const stem = logicalKey.replace(/\.png$/i, '');
  const url = (key) => `${base}/${key}`;
  const capture = captureManifestBase === base ? captureManifest.get(logicalKey) || null : null;
  const candidates = new Map();
  for (const variant of SRCSET_WIDTHS) {
    const width = capture ? Math.min(capture.width, variant.width) : variant.width;
    if (!candidates.has(width)) candidates.set(width, url(`${stem}${variant.suffix}`));
  }
  if (includeSource && capture) candidates.set(capture.width, url(logicalKey));
  return {
    capture,
    // The smallest derivative is the `src`, so a browser that ignores srcset
    // (or a surface that drops it) still gets the cheap file, never the PNG.
    src: url(`${stem}${SRCSET_WIDTHS[0].suffix}`),
    // The original full-size image. For the handful of places that take a bare
    // URL string and cannot carry a ladder — the delete-confirm dialog's single
    // context thumbnail — this is the value that always resolves, derivatives
    // uploaded or not. Never use it for a grid: it is the 721 KB file.
    source: url(logicalKey),
    srcset: [...candidates].map(([width, source]) => `${source} ${width}w`).join(', '),
    sizes,
    fallbacks: [url(logicalKey), ...extraFallbacks.map(url)]
  };
}

/**
 * Per-section screenshot of a template site.
 * Falls back to the template's homepage screenshot, which is the case that
 * matters: many templates have `preview.png` uploaded but no per-section shots.
 */
export function templateSectionThumbnail(siteName, sectionId, opts = {}) {
  const dir = templateDirName(siteName);
  if (!dir || !sectionId) return null;
  return descriptorFor(`template-thumbnails/${dir}/${sectionId}.png`, {
    ...opts,
    extraFallbacks: [`template-thumbnails/${dir}/preview.png`]
  });
}

/** A template's homepage screenshot. Terminal — nothing to fall back to. */
export function templatePreviewThumbnail(siteName, opts = {}) {
  const dir = templateDirName(siteName);
  if (!dir) return null;
  return descriptorFor(`template-thumbnails/${dir}/preview.png`, opts);
}

/** A CodeStitch catalog section thumbnail, keyed by its filename in the index. */
export function catalogSectionThumbnail(thumbnailFilename, opts = {}) {
  if (!thumbnailFilename) return null;
  return descriptorFor(`thumbnails/${thumbnailFilename}`, opts);
}

/**
 * A mobile-menu fragment's drawer capture (MM-4). Keyed deterministically by
 * the fragment's own catalogKey — `thumbnails/mm-*.png`, written by
 * generate-mobile-menu-thumbnails.js — so the generated section index never
 * needs a thumbnail field for fragments. Falls back to the source template's
 * homepage screenshot for a fragment whose capture has not shipped yet.
 */
export function mobileMenuThumbnail(entry, opts = {}) {
  const key = typeof entry?.catalogKey === 'string' ? entry.catalogKey : '';
  if (!key) return null;
  const dir = templateDirName(entry.templateSiteName);
  return descriptorFor(`thumbnails/${key}.png`, {
    ...opts,
    extraFallbacks: dir ? [`template-thumbnails/${dir}/preview.png`] : []
  });
}

/**
 * Wrap an already-resolved absolute URL (a custom template's signed thumbnail,
 * say) so callers can hand every surface the same shape. No derivatives exist
 * for these, so the descriptor is a plain single source.
 */
export function literalThumbnail(url, { sizes = DEFAULT_SIZES } = {}) {
  if (!url) return null;
  return { src: String(url), source: String(url), srcset: '', sizes, fallbacks: [] };
}

/**
 * The full-resolution ladder for a descriptor, for a surface that ENLARGES a
 * thumbnail (the template preview's lightbox) rather than shrinking it.
 *
 * `src` and `srcset` name the 400w/960w derivatives, which is right for a card
 * and soft at 1100px, so a lightbox must never reuse them. `fallbacks` already
 * IS this ladder — `descriptorFor` builds it as `[the original PNG, …extras]`,
 * where the extras are themselves originals (`preview.png`) — so a lightbox that
 * walks it lands on full-size pixels at every rung, including for a template
 * whose per-section capture was never taken.
 *
 * A literal descriptor carries no ladder at all; its single URL is the original.
 *
 * @param {object|null} descriptor
 * @returns {string[]} candidate URLs, best first
 */
export function fullSizeCandidates(descriptor) {
  if (!descriptor) return [];
  if (descriptor.fallbacks?.length) return [...descriptor.fallbacks];
  return descriptor.source ? [descriptor.source] : [];
}

/**
 * Advance an <img> one rung down its ladder, or give up exactly once.
 *
 * Two things here are load-bearing and both were caught by a failing test
 * rather than by reading the code:
 *
 * - `srcset` must be cleared before setting `src`. With candidates still
 *   attached the browser re-runs selection, picks a derivative again, and the
 *   PNG fallback is never actually requested — the image fails in a loop.
 * - The tracking attribute must survive the LAST candidate being applied.
 *   Removing it there swallows that candidate's own error, so `onExhausted`
 *   never runs and a dead image sits in the grid forever. Exhaustion is instead
 *   recorded on the element, which also makes giving up idempotent when several
 *   errors land for one image.
 */
function advanceThumbnail(img, onExhausted) {
  if (img.hasAttribute(THUMB_FAILED_ATTR)) return;
  const raw = img.getAttribute(FALLBACK_ATTR);
  const remaining = raw === null ? [] : raw.split('|').filter(Boolean);
  const next = remaining.shift();
  if (next === undefined) {
    img.removeAttribute(FALLBACK_ATTR);
    img.removeAttribute(LOADING_ATTR);
    img.setAttribute(THUMB_FAILED_ATTR, '1');
    onExhausted?.(img);
    return;
  }
  img.setAttribute(FALLBACK_ATTR, remaining.join('|'));
  img.removeAttribute('srcset');
  img.removeAttribute('sizes');
  img.src = next;
}

function markErrorHandled(event) {
  if (!event || typeof event !== 'object') return false;
  if (handledErrorEvents.has(event)) return true;
  handledErrorEvents.add(event);
  return false;
}

function clearThumbnailLoading(eventOrImg) {
  const img = eventOrImg?.target || eventOrImg;
  if (img instanceof HTMLImageElement) img.removeAttribute(LOADING_ATTR);
}

/**
 * Point an existing <img> at a descriptor and wire its retry ladder.
 *
 * @param {HTMLImageElement} img
 * @param {object|null} descriptor
 * @param {object} [opts]
 * @param {() => void} [opts.onExhausted]
 *   Runs when every candidate has failed. Each surface hides a dead thumbnail
 *   differently — a class swap on the box, a removed node — so the give-up
 *   action stays with the caller rather than being guessed at here.
 */
export function applyThumbnail(img, descriptor, { onExhausted } = {}) {
  if (!img || !descriptor) return;
  img.removeAttribute(THUMB_FAILED_ATTR);
  img.removeAttribute(FALLBACK_ATTR);
  img.removeAttribute('srcset');
  img.removeAttribute('sizes');
  // setAttribute rather than the IDL properties: reflection for `loading` and
  // `decoding` is not universal (jsdom does not implement it), and attributes
  // are what the markup path emits anyway, so both paths stay identical.
  img.setAttribute('loading', 'lazy');
  // Not decorative: a 1280px PNG decoded synchronously blocks the frame it
  // lands in, which is what made a grid of these janky even from cache.
  img.setAttribute('decoding', 'async');
  img.setAttribute(LOADING_ATTR, '');
  if (descriptor.srcset) {
    img.setAttribute('srcset', descriptor.srcset);
    img.setAttribute('sizes', descriptor.sizes);
  }
  if (descriptor.fallbacks.length) {
    img.setAttribute(FALLBACK_ATTR, descriptor.fallbacks.join('|'));
  }
  img.addEventListener('load', clearThumbnailLoading, { once: true });
  img.addEventListener('error', (event) => {
    if (markErrorHandled(event)) return;
    advanceThumbnail(img, () => onExhausted?.());
  });
  img.src = descriptor.src;
}

/**
 * Escaped attributes for surfaces that build their <img> as an HTML string.
 * Pair with `installThumbnailFallback()` on an ancestor — the ladder needs a
 * listener, and a delegated one beats the inline `onerror` these strings used
 * to carry.
 */
export function thumbnailAttrs(descriptor) {
  if (!descriptor) return '';
  const parts = [
    `src="${escapeHTML(descriptor.src)}"`,
    'loading="lazy"',
    'decoding="async"',
    `${LOADING_ATTR}=""`
  ];
  if (descriptor.srcset) {
    parts.push(`srcset="${escapeHTML(descriptor.srcset)}"`);
    parts.push(`sizes="${escapeHTML(descriptor.sizes)}"`);
  }
  if (descriptor.fallbacks.length) {
    parts.push(`${FALLBACK_ATTR}="${escapeHTML(descriptor.fallbacks.join('|'))}"`);
  }
  return parts.join(' ');
}

/**
 * Install one capture-phase error listener that walks the ladder for every
 * descendant <img> carrying fallbacks.
 *
 * Capture, because `error` does not bubble — a listener on the container only
 * sees it during the capture phase. One listener per container replaces an
 * inline handler per image.
 *
 * @param {Element} root
 * @param {(img: HTMLImageElement) => void} [onExhausted]
 * @returns {() => void} teardown
 */
export function installThumbnailFallback(root, onExhausted) {
  if (!root) return () => {};
  const onError = (event) => {
    const img = event.target;
    if (!(img instanceof HTMLImageElement)) return;
    if (!img.hasAttribute(FALLBACK_ATTR)) return;
    if (markErrorHandled(event)) return;
    advanceThumbnail(img, onExhausted);
  };
  root.addEventListener('error', onError, true);
  root.addEventListener('load', clearThumbnailLoading, true);
  return () => {
    root.removeEventListener('error', onError, true);
    root.removeEventListener('load', clearThumbnailLoading, true);
  };
}
